import type { NostrEvent } from '@nostrify/nostrify';

/** NIP-25 reaction (regular). Used here as Wikistar's "star" for wiki contributions. */
export const REACTION_KIND = 7;
/** NIP-09 deletion request (regular). Used to "unstar" by retracting a previous reaction. */
export const DELETION_KIND = 5;

/**
 * A reaction with `content` of `+` or an empty string MUST be interpreted as
 * a "like"/upvote per NIP-25. Wikistar treats these as "stars".
 */
export function isLikeReaction(event: NostrEvent): boolean {
  return event.kind === REACTION_KIND && (event.content === '+' || event.content === '');
}

/** The minimal identity of an event needed to build a star (reaction) or check reactions against it. */
export interface StarTarget {
  id: string;
  pubkey: string;
  kind: number;
  /** `kind:pubkey:d` coordinate, required when `kind` is addressable (30000-39999). */
  addressPointer?: string;
}

/**
 * Builds the tags for a NIP-25 "like" (star) reaction to `target`.
 * Includes the recommended `p` and `k` tags, plus an `a` tag when `target`
 * is an addressable event (per NIP-25: "If the event being reacted to is an
 * addressable event, an `a` SHOULD be included together with the `e` tag").
 */
export function buildStarTags(target: StarTarget): string[][] {
  const tags: string[][] = [
    ['e', target.id],
    ['p', target.pubkey],
    ['k', String(target.kind)],
  ];
  if (target.addressPointer) tags.push(['a', target.addressPointer]);
  return tags;
}
